<template>
  <div class="rpanel" :class="{ 'rpanel--full': panel.full }">
    <button type="button" class="rpanel__head" :class="{ 'rpanel__head--open': open }" @click="$emit('toggle')">
      <span class="rpanel__title">{{ panel.title }}</span>
      <span v-if="panel.hint" class="rpanel__hint">{{ panel.hint }}</span>
      <hero-icon :icon="ChevronDownIcon" :size="16" class="rpanel__caret" :class="{ 'rpanel__caret--up': open }" />
    </button>

    <div v-if="open" class="rpanel__body" :class="{ 'rpanel__body--flush': panel.kind === 'slot' }">
      <!-- an existing rich panel, folded in whole (map, drill-down and all) -->
      <slot v-if="panel.kind === 'slot'" />

      <!-- horizontal bars -->
      <div v-else-if="panel.kind === 'bars'" class="rpanel__bars">
        <div v-for="(b, i) in panel.rows" :key="i" class="rpanel__bar">
          <span class="rpanel__barlbl" :title="b.label">{{ b.label }}</span>
          <span class="rpanel__track"><span :style="{ width: b.width, background: b.color }"></span></span>
          <span class="rpanel__barval">{{ b.value }}</span>
        </div>
      </div>

      <!-- demand vs supply: two stacked tracks per row -->
      <div v-else-if="panel.kind === 'dual'" class="rpanel__dual">
        <div v-for="(b, i) in panel.rows" :key="i">
          <div class="rpanel__dualhead">
            <span class="rpanel__duallbl">{{ b.label }}</span>
            <span class="rpanel__dualnote">{{ b.note }}</span>
          </div>
          <div class="rpanel__dualtracks">
            <span class="rpanel__track rpanel__track--sm"><span :style="{ width: b.width, background: '#b52118' }"></span></span>
            <span class="rpanel__track rpanel__track--sm"><span :style="{ width: b.width2, background: '#c89b3c' }"></span></span>
          </div>
        </div>
        <div class="rpanel__legend">
          <span style="color: #b52118">▬ demanda</span>
          <span style="color: #a08033">▬ oferta</span>
        </div>
      </div>

      <!-- funnel: each step as a share of the first -->
      <div v-else-if="panel.kind === 'funnel'" class="rpanel__funnel">
        <div v-for="(f, i) in panel.rows" :key="i">
          <div class="rpanel__fhead">
            <span class="rpanel__flbl">{{ f.label }}</span>
            <span class="rpanel__fval">
              {{ f.value }}<span v-if="f.note" class="rpanel__fnote">{{ f.note }}</span>
            </span>
          </div>
          <div class="rpanel__track rpanel__track--lg"><span :style="{ width: f.width, background: '#1a1814' }"></span></div>
        </div>
      </div>

      <!-- plain stats -->
      <div v-else class="rpanel__stats">
        <div v-for="(s, i) in panel.rows" :key="i" class="rpanel__stat">
          <span class="rpanel__statval">{{ s.value }}</span>
          <span class="rpanel__statlbl">{{ s.label }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Level 3: one independent, collapsible detail panel. Four shapes cover every
// breakdown the report needs — ranked bars, demand-vs-supply pairs, a funnel,
// and a short list of standalone figures.
import HeroIcon from "@/components/shared/HeroIcon";
import ChevronDownIcon from "heroicons/24/outline/chevron-down.svg";

export default {
  components: { HeroIcon },

  props: {
    panel: {
      type: Object,
      required: true
    },
    open: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({ ChevronDownIcon })
};
</script>

<style scoped>
.rpanel--full { grid-column: 1 / -1; }
.rpanel {
  background: #fff;
  border: 1px solid #ece7de;
  border-radius: 14px;
  box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18);
  align-self: start;
  overflow: hidden;
}

.rpanel__head {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: none;
  border-bottom: 1px solid transparent;
  cursor: pointer;
  padding: 12px 16px;
  text-align: left;
  min-height: 46px;
}
.rpanel__head:hover { background: #f4f0e8; }
.rpanel__head--open { background: #faf8f4; border-bottom-color: #ece7de; }
.rpanel__title {
  font-size: 10.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #756f64;
  font-weight: 700;
}
.rpanel__head--open .rpanel__title { color: #56504a; }
.rpanel__hint { font-size: 11.5px; color: #a39a8c; }
.rpanel__caret {
  margin-left: auto;
  flex: none;
  color: #a39a8c;
  transition: transform 0.18s ease;
}
.rpanel__caret--up { transform: rotate(180deg); }

.rpanel__body { padding: 16px 18px 18px; }
.rpanel__body--flush { padding: 0; }

/* shared track */
.rpanel__track {
  height: 7px;
  border-radius: 999px;
  background: #f1ece3;
  overflow: hidden;
  display: block;
}
.rpanel__track > span { display: block; height: 100%; border-radius: 999px; }
.rpanel__track--sm { height: 6px; }
.rpanel__track--lg { height: 8px; }

.rpanel__bars { display: flex; flex-direction: column; gap: 9px; }
.rpanel__bar {
  display: grid;
  grid-template-columns: minmax(70px, max-content) 1fr 52px;
  align-items: center;
  gap: 11px;
}
.rpanel__barlbl {
  font-size: 12.5px;
  font-weight: 600;
  color: #1a1814;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 190px;
}
.rpanel__barval {
  font-size: 12.5px;
  font-weight: 700;
  color: #1a1814;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rpanel__dual { display: flex; flex-direction: column; gap: 12px; }
.rpanel__dualhead { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 4px; }
.rpanel__duallbl { font-size: 12.5px; font-weight: 600; color: #1a1814; }
.rpanel__dualnote { font-size: 11.5px; color: #756f64; }
.rpanel__dualtracks { display: flex; flex-direction: column; gap: 3px; }
.rpanel__legend { display: flex; gap: 16px; font-size: 11px; color: #756f64; }

.rpanel__funnel { display: flex; flex-direction: column; gap: 10px; }
.rpanel__fhead { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 4px; }
.rpanel__flbl { font-size: 12.5px; font-weight: 600; color: #1a1814; }
.rpanel__fval { font-size: 12.5px; font-weight: 700; color: #1a1814; font-variant-numeric: tabular-nums; }
.rpanel__fnote { font-size: 11px; font-weight: 600; color: #a39a8c; margin-left: 6px; }

.rpanel__stats { display: flex; flex-direction: column; gap: 14px; }
.rpanel__stat { display: flex; align-items: baseline; gap: 10px; }
.rpanel__statval {
  font-size: 22px;
  font-weight: 700;
  color: #1a1814;
  line-height: 1;
  min-width: 62px;
  font-variant-numeric: tabular-nums;
}
.rpanel__statlbl { font-size: 12.5px; color: #756f64; line-height: 1.35; }
</style>
